import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Icon from './Icon';
import Button from './Button';
import LoadingEllipsis from './LoadingEllipsis';
import { useGlobalState } from '../stateProvider/StateProvider';
import { IsAuthorized } from '../RoleProtected';
import { shallowCompare } from '../utility/functions';
import Row from '../layout/Row';
import Modal from '../Modal';
import Col from '../layout/Col';
import ModalFactory from '../Modal';

export const FormContext = React.createContext();

const I = styled(Icon)`
    margin-left: 0.5em;
`;
const Error = styled.div`
    font-size: 0.7em;
    padding: 0.2em 0.5em;
    color: ${({ theme }) => theme.error};
`
const Frm = styled.form`
    display: flex;
    flex-direction: ${({ direction }) => direction};
    gap: 0.7em;
    position: relative;
    ${({ direction }) => direction === 'row' ? 'align-items: flex-end;' : ''};
`
const SubmitButton = styled(Button)`
    position: sticky;
    bottom: ${({ diff, pos }) => diff ? '0' : (pos * -1) + 'px'};
    transition: all 0.3s ease-in-out;
    top: auto;
    z-index: 1;
`

const Form = ({ inputs, onSubmit, displayform, row, children, ...props }) => {

    const { state: { help } } = useGlobalState();

    const formRef = useRef();

    const [initial, setInitial] = useState(inputs);
    const [values, setValues] = useState(inputs);
    const [submitting, setSubmitting] = useState(false);
    const [editing, setEditing] = useState(props.editable);
    const [formError, setFormError] = useState('');
    const [dirty, setDirty] = useState(false);
    const [formHelp] = useState(help && props.name ? help[props.name] || {} : null);
    const [formName] = useState(props.name);
    const isAuthorized = IsAuthorized(props.roles);

    const [diff, setDiff] = useState(false);
    const [bound, setBound] = useState(false);

    useLayoutEffect(() => {
        if (formRef.current) {
            setBound(formRef.current.clientHeight);
        }
    }, []);

    useEffect(() => {
        if (JSON.stringify(inputs) !== JSON.stringify(initial)) {
            setValues(inputs);
            setInitial(inputs);
        }
    }, [inputs]);

    useEffect(() => {
        if (props.onInputs)
            props.onInputs(values, setValues)

        setDiff(!shallowCompare(values, initial))

        if (props.windowSubmission) {
            window[props.windowSubmission] = () => handleSubmit({ preventDefault: () => { } });
        }
    }, [values]);

    const [errors, setErrors] = useState(
        Object.fromEntries(Object.entries(inputs).map(([key, val]) => [key, false]))
    )

    useEffect(() => {
        setDirty(Object.keys(errors).some(v => errors[v] === true));
        // console.log(Object.keys(errors).filter(v => errors[v] === true))
    }, [errors]);

    useEffect(() => {
        if (formError && formRef.current) {
            formRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
        };
    }, [formError]);

    const resetErrors = () => {
        setErrors(
            Object.fromEntries(Object.entries(inputs).map(([key, val]) => [key, false]))
        )
    }

    const handleSubmit = async e => {
        e.preventDefault();
        if (dirty) return;
        setSubmitting(true);
        setFormError('');

        const disabledInputs = formRef.current.querySelectorAll('input[disabled], textarea[disabled], select[disabled]');
        const submittingValues = { ...values };
        Array.from(disabledInputs)
            .forEach(input => delete submittingValues[input.name]);

        try {
            if (props.localForm) {
                onSubmit(submittingValues, setFormError)
                setSubmitting(false)
            } else {
                onSubmit(submittingValues)
                    .then(() => {
                        if (props.editable)
                            setInitial(submittingValues);

                        if (props.clearInputs)
                            setValues(initial);
                    })
                    .catch(ex => {
                        if (ex.type === 'Canceled') {
                            setValues(initial);
                            return
                        }
                        if (ex.error?.errorMessage && ex.title) {
                            setFormError(
                                <Col>
                                    <b>{ex.title}</b>
                                    <span>{ex.error.errorMessage}</span>
                                </Col>
                            )
                        } else {
                            console.log(ex)
                            setFormError(ex.error?.errorMessage || ex.errorMessage || 'Oops, something went wrong.')
                        }
                    })
                    .finally(() => {
                        setSubmitting(false)
                        setEditing(props.editable)
                    })
            }
        }
        catch (er) {
            setFormError(er.errorMessage || 'Oops, something went wrong.')
            setSubmitting(false)
            setValues(initial);
        }
    }

    const toggleEditing = () => {
        if (editing) {
            setValues(initial);
        }
        setEditing(!editing);
    };

    const setValue = (key, val) => {
        setValues(v => {
            return {
                ...v,
                [key]: val
            }
        })
    }

    const contextValues = {
        values,
        submitting,
        errors,
        editing,
        initial,
        dirty,
        formError,
        formHelp,
        formName,
        setValues,
        setValue,
        setErrors,
        setEditing,
        setFormError,
        resetErrors,
    };

    const formChildren = typeof children === 'function' ? children(contextValues) : children;

    return (
        <Frm
            ref={formRef}
            onSubmit={(e) => handleSubmit(e)}
            direction={row === true ? 'row' : 'column'}
            {...props}
        >
            <FormContext.Provider value={contextValues}>
                {formChildren}
                <Row>
                    {props.formControls}
                    {props.editable && !props.hideEdit && isAuthorized && (
                        <Button
                            icon={editing ? 'edit' : 'times'}
                            color={editing ? null : 'error'}
                            style={{ zIndex: 1, ...props.buttonstyle }}
                            onClick={toggleEditing}
                        >
                            {editing ? 'Edit' : 'Cancel'}
                        </Button>
                    )}
                </Row>

                {
                    !editing && !props.hideEdit && (
                        <SubmitButton
                            pos={bound}
                            diff={diff && !(submitting || dirty)}
                            disabled={submitting || dirty}
                            type='submit'
                            icon={submitting ? 'spinner' : null}
                            style={props.buttonstyle}
                        >
                            {submitting ? (props.submitting || 'Submitting') : (props.text || 'Submit')}
                            {submitting && <LoadingEllipsis />}
                        </SubmitButton>
                    )}
                {!props.hideError && formError && <Error>{formError}</Error>}
            </FormContext.Provider>
        </Frm>
    );
}

export default Form;